import endOfMonth from "date-fns/endOfMonth";
import max from "date-fns/max";
import min from "date-fns/min";
import subMilliseconds from "date-fns/subMilliseconds";
import startOfMonth from "date-fns/startOfMonth";
import getHours from "date-fns/getHours";
import getMinutes from "date-fns/getMinutes";
import format from "date-fns/format";
import { useMemo } from "react";
import objectKeys from "../../../common/objectKeys";
import getAvailableTimeSlots from "../../../helpers/appointmentOperatingHours/getAvailableTimeSlots";
import {
  AvailableTimeSlots,
  CenterTimeSlot,
  Center,
  Appointment,
} from "./types";

const useAvailableTimeSlots = (params: {
  monthDate: Date;
  center: Center | null;
  appointment: Appointment | null;
  centerTimeSlots: CenterTimeSlot[] | null;
  statCutoffs?: Record<string, number>;
}) => {
  const { monthDate, appointment, center, centerTimeSlots, statCutoffs } =
    params;

  const availableTimeSlots = useMemo<AvailableTimeSlots>(() => {
    if (!center || !centerTimeSlots || !appointment) return {};

    const { dateLimits, operatingHours, timezoneOffsetMillis } = center;

    const timeSlots = centerTimeSlots.map((timeSlot) => {
      return {
        startedAt: timeSlot.started_at,
        endedAt: timeSlot.ended_at,
      };
    });

    const startMonthCenterTimezone = subMilliseconds(
      startOfMonth(monthDate),
      timezoneOffsetMillis,
    );

    const endMonthCenterTimezone = subMilliseconds(
      endOfMonth(monthDate),
      timezoneOffsetMillis,
    );

    const availableTimeSlotsData = getAvailableTimeSlots({
      startDate: max([startMonthCenterTimezone, dateLimits.minDateTimeUTC]),
      endDate: min([endMonthCenterTimezone, dateLimits.maxDateTimeUTC]),
      operatingHours,
      centerTimeSlots: timeSlots,
      timezoneOffsetMillis,
      appointmentDurationMinutes: appointment.durationMinutes,
      timezone: center.timezone,
    });

    const result = objectKeys(
      availableTimeSlotsData,
    ).reduce<AvailableTimeSlots>((acc, dayKey) => {
      let slots = availableTimeSlotsData[dayKey].available;
      const dateISO = format(
        new Date(
          monthDate.getFullYear(),
          monthDate.getMonth(),
          dayKey as number,
        ),
        "yyyy-MM-dd",
      );
      const cutoff = statCutoffs?.[dateISO];
      if (cutoff !== undefined) {
        slots = slots.filter(
          (slot) => getHours(slot) * 60 + getMinutes(slot) < cutoff,
        );
      }
      return { ...acc, [dayKey]: slots };
    }, {});

    return result;
  }, [center, centerTimeSlots, appointment, monthDate, statCutoffs]);

  return availableTimeSlots;
};

export default useAvailableTimeSlots;
